import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, Calculator, ArrowLeft } from "lucide-react";
import { Button } from "@/common/components/ui/button";
import { Card, CardContent } from "@/common/components/ui/card";
import { CompactCalculatorRow } from "../CompactCalculatorRow";
import { calculatePPF, formatCurrency } from "../../utils/calculations";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import ProductLayout from "@/common/components/ProductLayout";

export const PPFCalculator = () => {
  const navigate = useNavigate();
  const [ppfAmount, setPpfAmount] = useState(50000);
  const [ppfYears, setPpfYears] = useState(15);
  const ppfRate = 7.1;

  const ppfResult = useMemo(
    () => calculatePPF(ppfAmount, ppfYears, ppfRate),
    [ppfAmount, ppfYears]
  );

  const ppfPieData = [
    { name: "Invested", value: ppfResult.invested, color: "#94a3b8" },
    { name: "Interest", value: ppfResult.interest, color: "#4f46e5" },
  ];

  return (
    <ProductLayout>
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/calculators")}
              className="mb-4 pl-0 hover:bg-transparent hover:text-primary"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Calculators
            </Button>
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-3 text-primary">
                <Calculator className="h-8 w-8" />
              </div>
              <h1 className="text-3xl font-bold text-secondary">
                PPF Calculator
              </h1>
            </div>
          </div>

          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 lg:grid-cols-2">
              <Card className="border-border bg-card">
                <CardContent className="space-y-6 p-6 md:p-8">
                  <div className="mb-2 flex items-center gap-3">
                    <div className="rounded-xl bg-amber-100 p-3">
                      <Wallet className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-secondary">
                        PPF Calculator
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Public Provident Fund Savings
                      </p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <CompactCalculatorRow
                      label="Yearly investment"
                      value={ppfAmount}
                      onChange={setPpfAmount}
                      min={500}
                      max={150000}
                      step={500}
                      prefix="₹"
                      limitLabels={["₹500", "₹1.5 L (Limit)"]}
                      colorClass="text-[#00d09c]"
                    />

                    <CompactCalculatorRow
                      label="Time period (in years)"
                      value={ppfYears}
                      onChange={setPpfYears}
                      min={15}
                      max={50}
                      step={1}
                      suffix="Yr"
                      limitLabels={["15 years", "50 years"]}
                      colorClass="text-[#00d09c]"
                    />

                    <CompactCalculatorRow
                      label="Rate of interest"
                      value={ppfRate}
                      readOnly
                      suffix="%"
                      colorClass="text-slate-600"
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-6">
                <Card className="border-border bg-muted/20">
                  <CardContent className="p-8">
                    <div className="relative mb-6 grid grid-cols-2 gap-4">
                      <div className="absolute right-0 top-0 flex gap-4 text-[10px] font-bold uppercase tracking-wider">
                        {ppfPieData.map(item => (
                          <div
                            key={item.name}
                            className="flex items-center gap-1.5 opacity-80"
                          >
                            <div
                              className="h-1.5 w-2.5 rounded-full"
                              style={{ backgroundColor: item.color }}
                            />
                            <span>
                              {item.name === "Invested"
                                ? "Total investment"
                                : "Total interest"}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={ppfPieData}
                              cx="50%"
                              cy="50%"
                              innerRadius={55}
                              outerRadius={75}
                              dataKey="value"
                              stroke="none"
                            >
                              {ppfPieData.map(e => (
                                <Cell key={e.name} fill={e.color} />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(v: number | undefined) =>
                                v !== undefined ? formatCurrency(v) : ""
                              }
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex flex-col justify-center pt-8">
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-slate-500">
                              Invested amount
                            </span>
                            <span className="font-bold text-slate-700">
                              {formatCurrency(ppfResult.invested)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-slate-500">
                              Total interest
                            </span>
                            <span className="font-bold text-slate-700">
                              {formatCurrency(ppfResult.interest)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-slate-500">
                              Maturity value
                            </span>
                            <span className="font-bold text-slate-700">
                              {formatCurrency(ppfResult.maturity)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 flex justify-center">
                      <Button className="w-full max-w-[200px] rounded-xl bg-[#00d09c] py-6 font-black uppercase tracking-widest text-white shadow-lg shadow-[#00d09c]/20 transition-all hover:scale-105 hover:bg-[#00b084] active:scale-95">
                        SAVE TAX
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProductLayout>
  );
};
